import type {
  AnalyticsOverview,
  TwinsPerUser,
  Match,
  GrowthData,
  MatchDistribution,
  AdminTwin,
  AdminTwinDetail,
  TwinMatch,
  TwinMatchingInsight,
  PaginationInfo,
  UsersResponse,
  User,
  UserMatch,
  Twin,
  BillingOverview,
  RevenueTrendResponse,
  BillingPlan,
  BillingTransactionsResponse,
  BillingSettings,
  EmailEvent,
  EmailRule,
  EmailRulesResponse,
  EmailTemplate,
  CreateDemoUserPayload,
  DemoSourceUser,
  DemoUserSummary,
  BestPracticesContent,
  BestPracticesContentPayload,
  BlogPressArticle,
  BlogPressArticlePayload,
  BlogPressAccessRequest,
  BlogPressAccessRequestStatus,
  BlogPressContributorStatus,
  BlogPressClaimResult,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

/**
 * Thrown by fetchWithAuth instead of a plain Error so callers that need to
 * branch on the HTTP status (e.g. the contributor claim flow telling 404
 * "no approved request" apart from 409 "already linked elsewhere") can do
 * so, while every existing `err instanceof Error` / `err.message` call site
 * keeps working unchanged.
 */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function formatApiError(error: any): string {
  const detail = error?.detail;

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const location = Array.isArray(item.loc) ? item.loc.join('.') : '';
          const message = typeof item.msg === 'string' ? item.msg : JSON.stringify(item);
          return location ? `${location}: ${message}` : message;
        }
        return String(item);
      })
      .filter(Boolean);

    if (messages.length > 0) {
      return messages.join(' | ');
    }
  }

  if (detail && typeof detail === 'object') {
    return JSON.stringify(detail);
  }

  return `HTTP ${error?.status || 'error'}`;
}

class ApiService {
  private async fetchWithAuth(endpoint: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('admin_token');
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new ApiError(formatApiError({ ...error, status: response.status }), response.status);
    }
    
    return response.json();
  }

  // Analytics
  async getAnalyticsOverview(): Promise<AnalyticsOverview> {
    return this.fetchWithAuth('/api/admin/analytics/overview');
  }

  async getTwinsPerUser(): Promise<{ data: TwinsPerUser[] }> {
    return this.fetchWithAuth('/api/admin/analytics/twins-per-user');
  }

  async getMatches(
    limit?: number,
    range: { createdFrom?: string; createdTo?: string } = {},
  ): Promise<{ totalMatches: number; matches: Match[] }> {
    const q = new URLSearchParams();
    if (limit) q.set('limit', limit.toString());
    if (range.createdFrom) q.set('created_from', range.createdFrom);
    if (range.createdTo) q.set('created_to', range.createdTo);
    const qs = q.toString() ? `?${q.toString()}` : '';
    return this.fetchWithAuth(`/api/admin/analytics/matches${qs}`);
  }

  async getGrowthData(days?: number): Promise<{ data: GrowthData[] }> {
    const query = days ? `?days=${days}` : '';
    return this.fetchWithAuth(`/api/admin/analytics/growth${query}`);
  }

  async getMatchDistribution(): Promise<{ data: MatchDistribution[] }> {
    return this.fetchWithAuth('/api/admin/analytics/match-distribution');
  }

  async getTwins(params: {
    search?: string;
    twinType?: string;
    status?: string;
    createdFrom?: string;
    createdTo?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
  } = {}): Promise<{ twins: AdminTwin[]; pagination: PaginationInfo }> {
    const q = new URLSearchParams();
    if (params.search)   q.set('search', params.search);
    if (params.twinType) q.set('twin_type', params.twinType);
    if (params.status)   q.set('status', params.status);
    if (params.createdFrom) q.set('created_from', params.createdFrom);
    if (params.createdTo)   q.set('created_to', params.createdTo);
    if (params.page)     q.set('page', params.page.toString());
    if (params.limit)    q.set('limit', params.limit.toString());
    if (params.sortBy)   q.set('sort_by', params.sortBy);
    const qs = q.toString() ? `?${q.toString()}` : '';
    return this.fetchWithAuth(`/api/admin/twins${qs}`);
  }

  // Users
  async getUsers(params: {
    search?: string;
    userType?: string;
    joinedFrom?: string;
    joinedTo?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
  } = {}): Promise<UsersResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.set('search', params.search);
    if (params.userType) queryParams.set('user_type', params.userType);
    if (params.joinedFrom) queryParams.set('joined_from', params.joinedFrom);
    if (params.joinedTo) queryParams.set('joined_to', params.joinedTo);
    if (params.page) queryParams.set('page', params.page.toString());
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.sortBy) queryParams.set('sort_by', params.sortBy);
    
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.fetchWithAuth(`/api/admin/users${query}`);
  }

  async getUser(userId: string): Promise<User> {
    return this.fetchWithAuth(`/api/admin/users/${userId}`);
  }

  async getUserTwins(userId: string): Promise<{ twins: Twin[] }> {
    return this.fetchWithAuth(`/api/admin/users/${userId}/twins`);
  }

  async deleteUserTwin(
    userId: string,
    twinId: string,
  ): Promise<{
    message: string;
    userId: string;
    twinId: string;
    deletedTwinChats: number;
    deletedUserChats: number;
    deletedCacheEntries: number;
  }> {
    return this.fetchWithAuth(`/api/admin/users/${userId}/twins/${twinId}`, {
      method: 'DELETE',
    });
  }

  async getUserMatches(userId: string): Promise<{ userId: string; totalMatches: number; matches: UserMatch[] }> {
    return this.fetchWithAuth(`/api/admin/users/${userId}/matches`);
  }

  async getTwinDetail(twinId: string): Promise<{ twin: AdminTwinDetail }> {
    return this.fetchWithAuth(`/api/admin/twins/${twinId}`);
  }

  async getTwinMatches(twinId: string): Promise<{ twinId: string; totalMatches: number; matches: TwinMatch[] }> {
    return this.fetchWithAuth(`/api/admin/twins/${twinId}/matches`);
  }

  async getTwinMatchingInsights(twinId: string): Promise<{
    twinId: string;
    totalInsights: number;
    insights: TwinMatchingInsight[];
  }> {
    return this.fetchWithAuth(`/api/admin/twins/${twinId}/matching-insights`);
  }

  async resetTwinMatching(twinId: string): Promise<{
    twinId: string;
    deletedTwinChats: number;
    deletedUserChats: number;
    deletedCacheEntries: number;
    highestMatchReset: boolean;
  }> {
    return this.fetchWithAuth(`/api/admin/twins/${twinId}/reset-matching`, { method: 'POST' });
  }

  async deleteUser(userId: string): Promise<{
    message: string;
    userId: string;
    deletedTwins: number;
    deletedTwinChats: number;
    deletedUserChats: number;
    deletedCacheEntries: number;
    deletedMatchCycles: number;
    deletedProfileDocs: number;
  }> {
    return this.fetchWithAuth(`/api/admin/users/${userId}`, { method: 'DELETE' });
  }

  async updateUserStatus(userId: string, disabled: boolean): Promise<{ message: string }> {
    return this.fetchWithAuth(`/api/admin/users/${userId}/status?disabled=${disabled}`, {
      method: 'PATCH',
    });
  }

  // Billing
  async getBillingOverview(): Promise<BillingOverview> {
    return this.fetchWithAuth('/api/admin/billing/overview');
  }

  async getBillingRevenueTrend(
    months = 6,
    range: { createdFrom?: string; createdTo?: string } = {},
  ): Promise<RevenueTrendResponse> {
    const q = new URLSearchParams({ months: months.toString() });
    if (range.createdFrom) q.set('created_from', range.createdFrom);
    if (range.createdTo) q.set('created_to', range.createdTo);
    return this.fetchWithAuth(`/api/admin/billing/revenue-trend?${q.toString()}`);
  }

  async getBillingPlans(): Promise<{ plans: BillingPlan[] }> {
    return this.fetchWithAuth('/api/admin/billing/plans');
  }

  async getBillingSettings(): Promise<BillingSettings> {
    return this.fetchWithAuth('/api/admin/billing/settings');
  }

  async updateBillingSettings(payload: BillingSettings): Promise<BillingSettings> {
    return this.fetchWithAuth('/api/admin/billing/settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async getBillingTransactions(
    limit = 50,
    filters: { search?: string; createdFrom?: string; createdTo?: string } = {},
  ): Promise<BillingTransactionsResponse> {
    const q = new URLSearchParams({ limit: limit.toString() });
    if (filters.search?.trim()) q.set('search', filters.search.trim());
    if (filters.createdFrom) q.set('created_from', filters.createdFrom);
    if (filters.createdTo) q.set('created_to', filters.createdTo);
    return this.fetchWithAuth(`/api/admin/billing/transactions?${q.toString()}`);
  }

  // Content (public Best Practices page copy)
  async getBestPracticesContent(): Promise<BestPracticesContent> {
    return this.fetchWithAuth('/api/admin/best-practices');
  }

  async updateBestPracticesContent(
    payload: BestPracticesContentPayload
  ): Promise<BestPracticesContent> {
    return this.fetchWithAuth('/api/admin/best-practices', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  // Blog/Press articles
  async getBlogPressArticles(): Promise<BlogPressArticle[]> {
    return this.fetchWithAuth('/api/admin/blog-press');
  }

  async createBlogPressArticle(payload: BlogPressArticlePayload): Promise<BlogPressArticle> {
    return this.fetchWithAuth('/api/admin/blog-press', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateBlogPressArticle(
    articleId: string,
    payload: BlogPressArticlePayload
  ): Promise<BlogPressArticle> {
    return this.fetchWithAuth(`/api/admin/blog-press/${articleId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteBlogPressArticle(articleId: string): Promise<{ deleted: boolean; id: string }> {
    return this.fetchWithAuth(`/api/admin/blog-press/${articleId}`, {
      method: 'DELETE',
    });
  }

  async reorderBlogPressArticles(orderedIds: string[]): Promise<BlogPressArticle[]> {
    return this.fetchWithAuth('/api/admin/blog-press/reorder', {
      method: 'PUT',
      body: JSON.stringify({ ordered_ids: orderedIds }),
    });
  }

  // Blog/Press contributor access requests (admin-only; see
  // backend/app/api/admin/blog_press_access.py). Routes intentionally live
  // at /blog-press-access-requests and /blog-press-contributors, not nested
  // under /blog-press, to avoid colliding with the article routes above.
  async getBlogPressAccessRequests(params: {
    status?: BlogPressAccessRequestStatus;
    limit?: number;
    offset?: number;
  } = {}): Promise<BlogPressAccessRequest[]> {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.limit) q.set('limit', params.limit.toString());
    if (params.offset) q.set('offset', params.offset.toString());
    const qs = q.toString() ? `?${q.toString()}` : '';
    return this.fetchWithAuth(`/api/admin/blog-press-access-requests${qs}`);
  }

  async getBlogPressAccessRequest(requestId: string): Promise<BlogPressAccessRequest> {
    return this.fetchWithAuth(`/api/admin/blog-press-access-requests/${requestId}`);
  }

  async approveBlogPressAccessRequest(requestId: string): Promise<BlogPressAccessRequest> {
    return this.fetchWithAuth(`/api/admin/blog-press-access-requests/${requestId}/approve`, {
      method: 'POST',
    });
  }

  async rejectBlogPressAccessRequest(requestId: string, note?: string): Promise<BlogPressAccessRequest> {
    return this.fetchWithAuth(`/api/admin/blog-press-access-requests/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify(note?.trim() ? { note: note.trim() } : {}),
    });
  }

  // Blog/Press contributors (approved/revoked access requests)
  async getBlogPressContributors(params: {
    status?: BlogPressContributorStatus;
    limit?: number;
    offset?: number;
  } = {}): Promise<BlogPressAccessRequest[]> {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.limit) q.set('limit', params.limit.toString());
    if (params.offset) q.set('offset', params.offset.toString());
    const qs = q.toString() ? `?${q.toString()}` : '';
    return this.fetchWithAuth(`/api/admin/blog-press-contributors${qs}`);
  }

  async revokeBlogPressContributor(requestId: string): Promise<BlogPressAccessRequest> {
    return this.fetchWithAuth(`/api/admin/blog-press-contributors/${requestId}/revoke`, {
      method: 'POST',
    });
  }

  // Public-prefixed but auth_required (not /api/admin/*) - links the
  // caller's own authenticated account to an approved access request for
  // the same email. See backend/app/api/blog_press/access_requests.py.
  async claimBlogPressContributorAccess(): Promise<BlogPressClaimResult> {
    return this.fetchWithAuth('/api/blog-press/contributors/claim', { method: 'POST' });
  }

  // Demo users
  async getDemoUsers(): Promise<{ users: DemoUserSummary[] }> {
    return this.fetchWithAuth('/api/admin/demo-users');
  }

  async getDemoSourceUser(userId: string): Promise<DemoSourceUser> {
    return this.fetchWithAuth(`/api/admin/demo-users/source-users/${userId}`);
  }

  async createDemoUser(payload: CreateDemoUserPayload): Promise<{
    message: string;
    userId: string;
    email: string;
    createdTwinCount: number;
    createdTwinChatCount: number;
    createdChatCount: number;
  }> {
    return this.fetchWithAuth('/api/admin/demo-users', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteDemoUser(userId: string): Promise<{ message: string; userId: string }> {
    return this.fetchWithAuth(`/api/admin/demo-users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Email templates & routing
  async getEmailEvents(): Promise<{ events: EmailEvent[] }> {
    return this.fetchWithAuth('/api/admin/email/events');
  }

  async getEmailTemplates(): Promise<{ templates: EmailTemplate[] }> {
    return this.fetchWithAuth('/api/admin/email/templates');
  }

  async createEmailTemplate(payload: Omit<EmailTemplate, 'created_at' | 'updated_at'>): Promise<EmailTemplate> {
    return this.fetchWithAuth('/api/admin/email/templates', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateEmailTemplate(
    templateId: string,
    payload: Omit<EmailTemplate, 'id' | 'created_at' | 'updated_at'>
  ): Promise<EmailTemplate> {
    return this.fetchWithAuth(`/api/admin/email/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteEmailTemplate(templateId: string): Promise<{ message: string }> {
    return this.fetchWithAuth(`/api/admin/email/templates/${templateId}`, {
      method: 'DELETE',
    });
  }

  async getEmailRules(options?: { reset?: boolean }): Promise<EmailRulesResponse> {
    const query = options?.reset ? '?reset=true' : '';
    return this.fetchWithAuth(`/api/admin/email/rules${query}`);
  }

  async updateEmailRules(rules: EmailRule[]): Promise<{ rules: EmailRule[] }> {
    return this.fetchWithAuth('/api/admin/email/rules', {
      method: 'PUT',
      body: JSON.stringify({ rules }),
    });
  }

  async sendTestEmail(payload: {
    template_id: string;
    to_email: string;
    variables: Record<string, string | number>;
  }): Promise<{ message: string }> {
    return this.fetchWithAuth('/api/admin/email/test-send', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
}

export const api = new ApiService();
